import { addToast, Autocomplete, AutocompleteItem, Button } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useController, useForm } from 'react-hook-form';

import { VALIDATION_RULES } from '../../../constants/validationRules';
import { useSession } from '../../../hooks/useSession';
import { useGetOrganization, usePostProjectIdMember } from '../../../services/hooks';
import { getProjectId, getProjects } from '../../../services/services';
import { ProjectDto } from '../../../services/types';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import { ProjectMemberItem } from '../ProjectMemberItem';

interface Props {
  project: ProjectDto;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export const InviteProjectMemberFromOrganizationForm = ({ project, onCancel, onSuccess }: Props) => {
  const { user } = useSession();

  const { data: organization, isPending: isLoadingOrganization } = useGetOrganization();

  const members = (organization?.members ?? []).filter((member) => member.status === 'joined');

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { email: '' },
  });

  const { field, fieldState } = useController({ control, name: 'email', rules: VALIDATION_RULES.EMAIL });

  const { max = 0, used = 0 } = user?.subscription.limits.usersCount ?? {};

  const queryClient = useQueryClient();
  const { mutate, isPending } = usePostProjectIdMember();

  const filteredMembers = members.filter(
    (member) => member.user?.id !== user?.id && !project.members.some((m) => m.user?.id === member.user?.id),
  );

  const onSubmit = ({ email }: { email: string }) => {
    const member = members.find((member) => member.email === email);

    mutate(
      { id: project.id, requestBody: { email, role: member?.role === 'admin' ? 'owner' : (member?.role ?? 'owner') } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [getProjects.key] });
          queryClient.invalidateQueries({ queryKey: [getProjectId.key, project.id] });
          addToast({ title: 'The invitation was sent', color: 'success', variant: 'flat' });
          reset();
          onSuccess?.();
        },
        onError: (error) => {
          addToast({ title: getErrorMessage(error), color: 'danger', variant: 'flat' });
        },
      },
    );
  };

  return (
    <form noValidate className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <Autocomplete
        defaultItems={filteredMembers}
        maxListboxHeight={256}
        isLoading={isLoadingOrganization}
        placeholder="Select member"
        isInvalid={!!fieldState.error}
        defaultSelectedKey={field.value}
        selectedKey={field.value}
        onSelectionChange={(userEmail) => field.onChange(userEmail)}
      >
        {(member) => (
          <AutocompleteItem key={member.user?.email} variant="flat" textValue={member.user?.email}>
            <ProjectMemberItem member={member} />
          </AutocompleteItem>
        )}
      </Autocomplete>
      <div className="flex justify-end gap-4">
        <Button isDisabled={isPending} variant="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isDisabled={used >= max} isLoading={isPending} className="bg-foreground text-content1">
          <span>Invite</span>
        </Button>
      </div>
    </form>
  );
};
